import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import accountingIntegrationsService from '../services/accountingIntegrationsService';
import { Download, Upload, PencilSimple, Check, X, Info, WarningCircle, CheckCircle, Plugs, ArrowsClockwise } from '@phosphor-icons/react';

const PROVIDERS = [
  { value: 'tally', label: 'Tally' },
  { value: 'quickbooks_desktop', label: 'QuickBooks Desktop' },
];

const today = () => new Date().toISOString().split('T')[0];
const monthAgo = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return d.toISOString().split('T')[0];
};

function AccountingIntegrationsManager() {
  const [provider, setProvider] = useState('tally');
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [savingId, setSavingId] = useState(null);

  const [startDate, setStartDate] = useState(monthAgo());
  const [endDate, setEndDate] = useState(today());
  const [onlyNew, setOnlyNew] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [history, setHistory] = useState([]);

  const [importFile, setImportFile] = useState(null);
  const [importAsOfDate, setImportAsOfDate] = useState(today());
  const [importPreview, setImportPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importHistory, setImportHistory] = useState([]);
  const [importFileKey, setImportFileKey] = useState(0);

  const [qbStatus, setQbStatus] = useState(null);
  const [qbLoading, setQbLoading] = useState(true);
  const [qbConnecting, setQbConnecting] = useState(false);
  const [qbSyncing, setQbSyncing] = useState(false);

  const [qbMappings, setQbMappings] = useState([]);
  const [qbMappingsLoading, setQbMappingsLoading] = useState(false);
  const [qbEditingMappingId, setQbEditingMappingId] = useState(null);
  const [qbEditValue, setQbEditValue] = useState('');
  const [qbSavingMappingId, setQbSavingMappingId] = useState(null);

  const [qbPushStart, setQbPushStart] = useState(monthAgo());
  const [qbPushEnd, setQbPushEnd] = useState(today());
  const [qbPushOnlyNew, setQbPushOnlyNew] = useState(true);
  const [qbPushing, setQbPushing] = useState(false);
  const [qbPushResult, setQbPushResult] = useState(null);
  const [qbJournalHistory, setQbJournalHistory] = useState([]);

  const loadQuickBooksStatus = useCallback(async () => {
    try {
      setQbLoading(true);
      const data = await accountingIntegrationsService.quickbooksStatus();
      setQbStatus(data);
    } catch (error) {
      // non-critical — QuickBooks may not be configured for this deployment
    } finally {
      setQbLoading(false);
    }
  }, []);

  const handleQuickBooksConnect = async () => {
    setQbConnecting(true);
    try {
      const { authorization_url } = await accountingIntegrationsService.quickbooksConnect();
      window.location.href = authorization_url;
    } catch (error) {
      toast.error(error.message || 'Failed to start QuickBooks connection');
      setQbConnecting(false);
    }
  };

  const handleQuickBooksSync = async () => {
    setQbSyncing(true);
    try {
      const result = await accountingIntegrationsService.quickbooksSyncChartOfAccounts();
      toast.success(`Synced: ${result.created} account(s) created, ${result.skipped} already existed`);
      loadQuickBooksStatus();
    } catch (error) {
      toast.error(error.message || 'Failed to sync chart of accounts');
    } finally {
      setQbSyncing(false);
    }
  };

  const handleQuickBooksDisconnect = async () => {
    try {
      await accountingIntegrationsService.quickbooksDisconnect();
      toast.success('QuickBooks disconnected');
      loadQuickBooksStatus();
    } catch (error) {
      toast.error(error.message || 'Failed to disconnect QuickBooks');
    }
  };

  useEffect(() => {
    loadQuickBooksStatus();
    if (new URLSearchParams(window.location.search).get('quickbooks') === 'connected') {
      toast.success('QuickBooks connected');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [loadQuickBooksStatus]);

  const loadQuickBooksMappings = useCallback(async () => {
    try {
      setQbMappingsLoading(true);
      const data = await accountingIntegrationsService.quickbooksAccountMappings();
      setQbMappings(Array.isArray(data) ? data : []);
    } catch (error) {
      // non-critical
    } finally {
      setQbMappingsLoading(false);
    }
  }, []);

  const loadQuickBooksJournalHistory = useCallback(async () => {
    try {
      const data = await accountingIntegrationsService.quickbooksJournalSyncHistory(10);
      setQbJournalHistory(Array.isArray(data) ? data : []);
    } catch (error) {
      // non-critical
    }
  }, []);

  useEffect(() => {
    if (qbStatus?.connected) {
      loadQuickBooksMappings();
      loadQuickBooksJournalHistory();
    }
  }, [qbStatus?.connected, loadQuickBooksMappings, loadQuickBooksJournalHistory]);

  const startEditQbMapping = (row) => {
    setQbEditingMappingId(row.gl_account_id);
    setQbEditValue(row.qb_account_id || '');
  };

  const cancelEditQbMapping = () => {
    setQbEditingMappingId(null);
    setQbEditValue('');
  };

  const saveQbMapping = async (row) => {
    if (!qbEditValue.trim()) {
      toast.error('QuickBooks Account ID cannot be empty');
      return;
    }
    setQbSavingMappingId(row.gl_account_id);
    try {
      await accountingIntegrationsService.quickbooksUpsertAccountMapping({
        gl_account_id: row.gl_account_id,
        qb_account_id: qbEditValue.trim(),
      });
      toast.success('Mapping saved');
      setQbEditingMappingId(null);
      loadQuickBooksMappings();
    } catch (error) {
      toast.error(error.message || 'Failed to save mapping');
    } finally {
      setQbSavingMappingId(null);
    }
  };

  const removeQbMapping = async (row) => {
    if (!row.mapping_id) return;
    try {
      await accountingIntegrationsService.quickbooksDeleteAccountMapping(row.mapping_id);
      toast.success('Mapping removed');
      loadQuickBooksMappings();
    } catch (error) {
      toast.error('Failed to remove mapping');
    }
  };

  const handleQuickBooksPush = async () => {
    if (!qbPushStart || !qbPushEnd) {
      toast.error('Select a date range');
      return;
    }
    setQbPushing(true);
    setQbPushResult(null);
    try {
      const result = await accountingIntegrationsService.quickbooksPushJournalEntries(
        `${qbPushStart}T00:00:00`, `${qbPushEnd}T23:59:59`, qbPushOnlyNew,
      );
      setQbPushResult(result);
      if (result.pushed > 0) {
        toast.success(`Pushed ${result.pushed} entr${result.pushed === 1 ? 'y' : 'ies'} to QuickBooks`);
      } else if (result.skipped > 0) {
        toast.error(`${result.skipped} entr${result.skipped === 1 ? 'y' : 'ies'} could not be pushed — see details below`);
      } else {
        toast.success('Nothing new to push for this range');
      }
      loadQuickBooksJournalHistory();
    } catch (error) {
      toast.error(error.message || 'Failed to push journal entries');
    } finally {
      setQbPushing(false);
    }
  };

  const loadMappings = useCallback(async () => {
    try {
      setLoading(true);
      const data = await accountingIntegrationsService.listMappings(provider);
      setMappings(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error('Failed to load account mappings');
    } finally {
      setLoading(false);
    }
  }, [provider]);

  const loadHistory = useCallback(async () => {
    try {
      const data = await accountingIntegrationsService.exportHistory(provider, 10);
      setHistory(Array.isArray(data) ? data : []);
    } catch (error) {
      // non-critical
    }
  }, [provider]);

  const loadImportHistory = useCallback(async () => {
    try {
      const data = await accountingIntegrationsService.openingBalanceHistory();
      setImportHistory(Array.isArray(data) ? data : []);
    } catch (error) {
      // non-critical
    }
  }, []);

  useEffect(() => {
    loadMappings();
    loadHistory();
    loadImportHistory();
  }, [loadMappings, loadHistory, loadImportHistory]);

  const handleFileSelect = (file) => {
    setImportFile(file);
    setImportPreview(null);
  };

  const handlePreviewImport = async () => {
    if (!importFile) {
      toast.error('Choose a CSV file first');
      return;
    }
    setPreviewing(true);
    try {
      const result = await accountingIntegrationsService.previewOpeningBalanceImport(provider, importFile);
      setImportPreview(result);
      if (result.error) {
        toast.error(result.error);
      } else if (result.unmatched.length > 0) {
        toast.error(`${result.unmatched.length} account(s) couldn't be matched — map them below or fix the file`);
      } else if (!result.is_balanced) {
        toast.error("Debits and credits don't match in this file");
      } else {
        toast.success(`${result.matched.length} account(s) ready to import`);
      }
    } catch (error) {
      toast.error(error.message || 'Failed to preview file');
    } finally {
      setPreviewing(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!importPreview || importPreview.unmatched.length > 0 || !importPreview.is_balanced) {
      toast.error('Resolve the preview issues before importing');
      return;
    }
    setImporting(true);
    try {
      const result = await accountingIntegrationsService.confirmOpeningBalanceImport(provider, importAsOfDate, importFile);
      toast.success(`Imported ${result.accounts_imported} opening balance(s) as journal entry ${result.journal_entry_id.slice(0, 8)}`);
      setImportFile(null);
      setImportPreview(null);
      setImportFileKey(k => k + 1);
      loadImportHistory();
    } catch (error) {
      toast.error(error.message || 'Failed to import opening balances');
    } finally {
      setImporting(false);
    }
  };

  const startEdit = (row) => {
    setEditingId(row.gl_account_id);
    setEditValue(row.external_name);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue('');
  };

  const saveMapping = async (row) => {
    if (!editValue.trim()) {
      toast.error('Name cannot be empty');
      return;
    }
    setSavingId(row.gl_account_id);
    try {
      await accountingIntegrationsService.upsertMapping({
        gl_account_id: row.gl_account_id,
        provider,
        external_name: editValue.trim(),
      });
      toast.success('Mapping saved');
      setEditingId(null);
      loadMappings();
    } catch (error) {
      toast.error(error.message || 'Failed to save mapping');
    } finally {
      setSavingId(null);
    }
  };

  const resetMapping = async (row) => {
    if (!row.mapping_id) return;
    try {
      await accountingIntegrationsService.deleteMapping(row.mapping_id);
      toast.success('Reverted to account name');
      loadMappings();
    } catch (error) {
      toast.error('Failed to reset mapping');
    }
  };

  const handleExport = async () => {
    if (!startDate || !endDate) {
      toast.error('Select a date range');
      return;
    }
    setExporting(true);
    try {
      const params = {
        start_date: `${startDate}T00:00:00`,
        end_date: `${endDate}T23:59:59`,
        only_new: onlyNew,
      };
      if (provider === 'tally') {
        await accountingIntegrationsService.exportTally(params);
      } else {
        await accountingIntegrationsService.exportQuickBooksDesktop(params);
      }
      toast.success('Export downloaded');
      loadHistory();
    } catch (error) {
      toast.error(error.message || 'Nothing to export for this range');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="accounting-integrations">
      <div style={{ background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: 8, padding: '12px 16px', marginBottom: 20, display: 'flex', gap: 10 }}>
        <Info size={20} style={{ flexShrink: 0, color: '#92400E' }} />
        <div style={{ fontSize: 13, color: '#92400E' }}>
          <strong>One-way, file-based export.</strong> Neither Tally nor QuickBooks Desktop has a live API this app can push into —
          this generates an import file your accountant brings into their own Tally/QuickBooks install
          (Tally: Gateway of Tally → Import Data → Vouchers. QuickBooks: File → Utilities → Import → IIF Files).
          The exact debit/credit polarity in each format should be verified against a real test import before relying on it for production data.
        </div>
      </div>

      <div className="table-container" style={{ padding: 16, marginBottom: 24 }}>
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 0 }}>
          <Plugs size={20} />
          QuickBooks Online
        </h3>
        <p style={{ color: 'var(--text-secondary, #666)', marginBottom: 12 }}>
          A live, one-way pull of your QuickBooks Online chart of accounts into Campusio — unlike the file exports above, this connects directly via API.
        </p>
        {qbLoading ? (
          <p>Checking connection…</p>
        ) : qbStatus?.connected ? (
          <div>
            <p style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#065F46' }}>
              <CheckCircle size={18} weight="fill" />
              Connected ({qbStatus.environment}) — realm {qbStatus.realm_id}
              {qbStatus.last_synced_at && ` · last synced ${new Date(qbStatus.last_synced_at).toLocaleString()}`}
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" onClick={handleQuickBooksSync} disabled={qbSyncing}>
                <ArrowsClockwise size={18} />
                {qbSyncing ? 'Syncing…' : 'Sync Chart of Accounts'}
              </button>
              <button className="btn btn-secondary" onClick={handleQuickBooksDisconnect}>Disconnect</button>
            </div>

            <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #e5e7eb' }}>
              <h4 style={{ marginTop: 0, marginBottom: 4 }}>Account Mapping</h4>
              <p style={{ fontSize: 13, color: '#6b7280', marginTop: 0, marginBottom: 10 }}>
                Which QuickBooks account (by its Account ID) each of our GL accounts posts to when pushing journal entries.
                Accounts pulled in via "Sync Chart of Accounts" are mapped automatically — anything else needs an ID set here first.
              </p>
              {qbMappingsLoading ? <p>Loading…</p> : (
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr><th>Code</th><th>Account Name</th><th>QuickBooks Account ID</th><th>Actions</th></tr>
                    </thead>
                    <tbody>
                      {qbMappings.map(row => (
                        <tr key={row.gl_account_id}>
                          <td>{row.account_code}</td>
                          <td>{row.account_name}</td>
                          <td>
                            {qbEditingMappingId === row.gl_account_id ? (
                              <input type="text" value={qbEditValue} onChange={e => setQbEditValue(e.target.value)} style={{ width: '100%' }} autoFocus />
                            ) : (
                              <span style={{ fontStyle: row.qb_account_id ? 'normal' : 'italic', color: row.qb_account_id ? 'inherit' : '#9ca3af' }}>
                                {row.qb_account_id || 'Not mapped'}
                              </span>
                            )}
                          </td>
                          <td className="action-buttons">
                            {qbEditingMappingId === row.gl_account_id ? (
                              <>
                                <button className="btn-icon" title="Save" onClick={() => saveQbMapping(row)} disabled={qbSavingMappingId === row.gl_account_id}><Check size={18} /></button>
                                <button className="btn-icon" title="Cancel" onClick={cancelEditQbMapping}><X size={18} /></button>
                              </>
                            ) : (
                              <>
                                <button className="btn-icon" title="Edit" onClick={() => startEditQbMapping(row)}><PencilSimple size={18} /></button>
                                {row.mapping_id && <button className="btn-icon" title="Remove mapping" onClick={() => removeQbMapping(row)}><X size={18} /></button>}
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #e5e7eb' }}>
              <h4 style={{ marginTop: 0, marginBottom: 4 }}>Push Journal Entries</h4>
              <p style={{ fontSize: 13, color: '#6b7280', marginTop: 0, marginBottom: 10 }}>
                Sends posted journal entries in the selected range to QuickBooks Online as journal entries — the other half of the sync.
                An entry with an unmapped GL account is skipped, not the whole batch.
              </p>
              <div className="form-row" style={{ flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
                <div className="form-group" style={{ minWidth: 160 }}>
                  <label>Start Date</label>
                  <input type="date" value={qbPushStart} onChange={e => setQbPushStart(e.target.value)} />
                </div>
                <div className="form-group" style={{ minWidth: 160 }}>
                  <label>End Date</label>
                  <input type="date" value={qbPushEnd} onChange={e => setQbPushEnd(e.target.value)} />
                </div>
                <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input type="checkbox" id="qb-only-new" checked={qbPushOnlyNew} onChange={e => setQbPushOnlyNew(e.target.checked)} style={{ width: 'auto' }} />
                  <label htmlFor="qb-only-new" style={{ margin: 0 }}>Only entries not pushed before</label>
                </div>
                <div className="form-group">
                  <button className="btn btn-primary" onClick={handleQuickBooksPush} disabled={qbPushing}>
                    <ArrowsClockwise size={16} /> {qbPushing ? 'Pushing…' : 'Push to QuickBooks'}
                  </button>
                </div>
              </div>

              {qbPushResult && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ display: 'flex', gap: 20, fontSize: 13, marginBottom: qbPushResult.errors?.length ? 8 : 0 }}>
                    <span>Pushed: <strong style={{ color: '#059669' }}>{qbPushResult.pushed}</strong></span>
                    <span>Skipped: <strong style={{ color: qbPushResult.skipped ? '#DC2626' : 'inherit' }}>{qbPushResult.skipped}</strong></span>
                  </div>
                  {qbPushResult.errors?.length > 0 && (
                    <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 8, padding: 12 }}>
                      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: '#991B1B' }}>
                        {qbPushResult.errors.map((err, i) => <li key={i}>{err}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {qbJournalHistory.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <div className="table-container">
                    <table className="data-table">
                      <thead><tr><th>Pushed At</th><th>QuickBooks Journal Entry ID</th><th>By</th></tr></thead>
                      <tbody>
                        {qbJournalHistory.map(row => (
                          <tr key={row.id}>
                            <td>{new Date(row.synced_at).toLocaleString()}</td>
                            <td>{row.qb_journal_entry_id}</td>
                            <td>{row.synced_by}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <button className="btn btn-primary" onClick={handleQuickBooksConnect} disabled={qbConnecting}>
            <Plugs size={18} />
            {qbConnecting ? 'Redirecting…' : 'Connect QuickBooks Online'}
          </button>
        )}
      </div>

      <div className="filters-bar" style={{ marginBottom: 16 }}>
        {PROVIDERS.map(p => (
          <button
            key={p.value}
            className={`btn ${provider === p.value ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setProvider(p.value)}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="form-card" style={{ padding: 16, marginBottom: 20 }}>
        <h3 style={{ marginTop: 0 }}>Export {PROVIDERS.find(p => p.value === provider)?.label}</h3>
        <div className="form-row" style={{ flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
          <div className="form-group" style={{ minWidth: 160 }}>
            <label>Start Date</label>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
          </div>
          <div className="form-group" style={{ minWidth: 160 }}>
            <label>End Date</label>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
          </div>
          <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" id="only-new" checked={onlyNew} onChange={e => setOnlyNew(e.target.checked)} style={{ width: 'auto' }} />
            <label htmlFor="only-new" style={{ margin: 0 }}>Only entries not exported before</label>
          </div>
          <div className="form-group">
            <button className="btn btn-primary" onClick={handleExport} disabled={exporting}>
              <Download size={16} /> {exporting ? 'Exporting...' : `Export ${provider === 'tally' ? 'XML' : 'IIF'}`}
            </button>
          </div>
        </div>

        {history.length > 0 && (
          <div style={{ marginTop: 12, fontSize: 12, color: '#6b7280' }}>
            Last export: {new Date(history[0].exported_at).toLocaleString()} by {history[0].exported_by} ({history.length} entries logged recently)
          </div>
        )}
      </div>

      <div className="form-card" style={{ padding: 16 }}>
        <h3 style={{ marginTop: 0 }}>Account Name Mapping</h3>
        <p style={{ fontSize: 13, color: '#6b7280', marginTop: -8 }}>
          {PROVIDERS.find(p => p.value === provider)?.label} identifies accounts by name. Override the name used in the export
          for any account whose ledger/account name differs on the other side — accounts without an override use their own name as-is.
        </p>
        {loading ? (
          <p>Loading...</p>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Account Name</th>
                  <th>{PROVIDERS.find(p => p.value === provider)?.label} Name</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {mappings.map(row => (
                  <tr key={row.gl_account_id}>
                    <td>{row.account_code}</td>
                    <td>{row.account_name}</td>
                    <td>
                      {editingId === row.gl_account_id ? (
                        <input
                          type="text"
                          value={editValue}
                          onChange={e => setEditValue(e.target.value)}
                          style={{ width: '100%' }}
                          autoFocus
                        />
                      ) : (
                        <span style={{ fontStyle: row.is_override ? 'normal' : 'italic', color: row.is_override ? 'inherit' : '#9ca3af' }}>
                          {row.external_name}{!row.is_override && ' (default)'}
                        </span>
                      )}
                    </td>
                    <td className="action-buttons">
                      {editingId === row.gl_account_id ? (
                        <>
                          <button className="btn-icon" title="Save" onClick={() => saveMapping(row)} disabled={savingId === row.gl_account_id}>
                            <Check size={18} />
                          </button>
                          <button className="btn-icon" title="Cancel" onClick={cancelEdit}>
                            <X size={18} />
                          </button>
                        </>
                      ) : (
                        <>
                          <button className="btn-icon" title="Edit" onClick={() => startEdit(row)}>
                            <PencilSimple size={18} />
                          </button>
                          {row.is_override && (
                            <button className="btn-icon" title="Reset to default" onClick={() => resetMapping(row)}>
                              <X size={18} />
                            </button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="form-card" style={{ padding: 16, marginTop: 20 }}>
        <h3 style={{ marginTop: 0 }}>Import Opening Balances</h3>
        <p style={{ fontSize: 13, color: '#6b7280', marginTop: -8 }}>
          One-time only, for moving a school onto Campusio from {PROVIDERS.find(p => p.value === provider)?.label}.
          Upload a Trial Balance export as CSV — this creates a single opening journal entry and does not
          import historical transactions. Unmatched accounts must be given a name mapping above before they can be imported.
        </p>
        <div className="form-row" style={{ flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
          <div className="form-group" style={{ minWidth: 220 }}>
            <label>Trial Balance CSV</label>
            <input
              key={importFileKey}
              type="file"
              accept=".csv,text/csv"
              onChange={e => handleFileSelect(e.target.files?.[0] || null)}
            />
          </div>
          <div className="form-group" style={{ minWidth: 160 }}>
            <label>As Of Date</label>
            <input type="date" value={importAsOfDate} onChange={e => setImportAsOfDate(e.target.value)} />
          </div>
          <div className="form-group">
            <button className="btn btn-secondary" onClick={handlePreviewImport} disabled={!importFile || previewing}>
              <Upload size={16} /> {previewing ? 'Checking...' : 'Preview'}
            </button>
          </div>
        </div>

        {importPreview && !importPreview.error && (
          <div style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', gap: 20, fontSize: 13, marginBottom: 10, flexWrap: 'wrap' }}>
              <span>Matched: <strong>{importPreview.matched.length}</strong></span>
              <span>Unmatched: <strong style={{ color: importPreview.unmatched.length ? '#DC2626' : 'inherit' }}>{importPreview.unmatched.length}</strong></span>
              <span>Total Debit: <strong>{importPreview.total_debit.toFixed(2)}</strong></span>
              <span>Total Credit: <strong>{importPreview.total_credit.toFixed(2)}</strong></span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: importPreview.is_balanced ? '#059669' : '#DC2626' }}>
                {importPreview.is_balanced ? <CheckCircle size={16} /> : <WarningCircle size={16} />}
                {importPreview.is_balanced ? 'Balanced' : 'Not balanced'}
              </span>
            </div>

            {importPreview.unmatched.length > 0 && (
              <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#991B1B', marginBottom: 6 }}>
                  Unmatched accounts — add a mapping above with these exact names, then re-preview:
                </div>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: '#991B1B' }}>
                  {importPreview.unmatched.map((row, i) => (
                    <li key={i}>{row.external_name} (Dr {row.debit.toFixed(2)} / Cr {row.credit.toFixed(2)})</li>
                  ))}
                </ul>
              </div>
            )}

            <button
              className="btn btn-primary"
              onClick={handleConfirmImport}
              disabled={importing || importPreview.unmatched.length > 0 || !importPreview.is_balanced}
            >
              {importing ? 'Importing...' : 'Confirm Import'}
            </button>
          </div>
        )}

        {importHistory.length > 0 && (
          <div style={{ marginTop: 20 }}>
            <h4 style={{ marginBottom: 8 }}>Import History</h4>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Provider</th>
                    <th>As Of</th>
                    <th>Accounts</th>
                    <th>Debit</th>
                    <th>Credit</th>
                    <th>Imported By</th>
                  </tr>
                </thead>
                <tbody>
                  {importHistory.map(row => (
                    <tr key={row.id}>
                      <td>{new Date(row.imported_at).toLocaleString()}</td>
                      <td>{PROVIDERS.find(p => p.value === row.provider?.toLowerCase())?.label || row.provider}</td>
                      <td>{row.as_of_date}</td>
                      <td>{row.row_count}</td>
                      <td>{row.total_debit.toFixed(2)}</td>
                      <td>{row.total_credit.toFixed(2)}</td>
                      <td>{row.imported_by}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AccountingIntegrationsManager;
